import { useState } from "react";
import {
  captureAuthState,
  detectLoginForm,
  type AuthStateSummary,
  type CaptureAuthStateInput,
  type LoginFormDetection,
  type LoginSelectorKind,
} from "../lib/api";

/**
 * Login Recipe capture dialog (Plan §22 Login UX, Option A).
 *
 * Customer types: label, login URL, three CSS selectors, test creds.
 * On submit the API runs a one-shot headless Playwright login, captures
 * `storageState`, encrypts + stores in the vault, returns the summary.
 *
 * Security framing in the UI is explicit:
 *   - "Use a dedicated test account, never your prod password."
 *   - The "remember credentials" opt-in is unchecked by default and
 *     called out separately with a what-it-does sentence.
 */
export function AuthStateCaptureDialog(props: {
  defaultLoginUrl?: string;
  onClose: () => void;
  onCaptured: (state: AuthStateSummary) => void;
}): JSX.Element {
  const { defaultLoginUrl, onClose, onCaptured } = props;
  const [label, setLabel] = useState("");
  const [loginUrl, setLoginUrl] = useState(defaultLoginUrl ?? "");
  const [usernameSelector, setUsernameSelector] = useState("input[type=email], input[name=email], input[name=username]");
  const [passwordSelector, setPasswordSelector] = useState("input[type=password]");
  const [submitSelector, setSubmitSelector] = useState("button[type=submit]");
  const [postLoginUrlContains, setPostLoginUrlContains] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [detectErr, setDetectErr] = useState<string | null>(null);
  // Per-slot kind badge ("auto-detected as id"), kept in state so a
  // subsequent manual edit clears it. Tracks the source of the
  // current value, NOT history.
  // `| undefined` is intentional — `exactOptionalPropertyTypes` (TS
  // strict) treats `username?: T` as "may be missing" but DISallows
  // explicit `{ username: undefined }`. We need to be able to clear
  // a field on edit, so widen each slot to allow the literal undefined.
  const [detectedKinds, setDetectedKinds] = useState<{
    username?: LoginSelectorKind | undefined;
    password?: LoginSelectorKind | undefined;
    submit?: LoginSelectorKind | undefined;
  }>({});
  const [pageTitle, setPageTitle] = useState("");

  async function handleDetect(): Promise<void> {
    if (!loginUrl.trim()) {
      setDetectErr("Enter a login URL first");
      return;
    }
    setDetecting(true);
    setDetectErr(null);
    try {
      const det: LoginFormDetection = await detectLoginForm({
        loginUrl: loginUrl.trim(),
      });
      setPageTitle(det.pageTitle);
      if (!det.found) {
        setDetectErr(
          "Couldn't find a login form on this URL. The page may be a multi-step flow, behind a captcha, or render the form via JS later than we waited.",
        );
        return;
      }
      // Pre-fill the selectors. A user-typed value wins ONLY if it
      // already matches the auto-detected one (rare); otherwise we
      // overwrite — the user clicked "detect", they expect a fresh
      // suggestion. Track the kind so the badge reflects the new value.
      if (det.username.value) {
        setUsernameSelector(det.username.value);
      }
      if (det.password.value) {
        setPasswordSelector(det.password.value);
      }
      if (det.submit.value) {
        setSubmitSelector(det.submit.value);
      }
      setDetectedKinds({
        ...(det.username.value ? { username: det.username.kind } : {}),
        ...(det.password.value ? { password: det.password.kind } : {}),
        ...(det.submit.value ? { submit: det.submit.kind } : {}),
      });
    } catch (e) {
      setDetectErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDetecting(false);
    }
  }

  const canSubmit =
    !busy &&
    label.trim() !== "" &&
    loginUrl.trim() !== "" &&
    usernameSelector.trim() !== "" &&
    passwordSelector.trim() !== "" &&
    submitSelector.trim() !== "" &&
    username !== "" &&
    password !== "";

  async function handleSubmit(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const input: CaptureAuthStateInput = {
        label: label.trim(),
        loginUrl: loginUrl.trim(),
        usernameSelector: usernameSelector.trim(),
        passwordSelector: passwordSelector.trim(),
        submitSelector: submitSelector.trim(),
        username,
        password,
        remember,
      };
      if (postLoginUrlContains.trim()) {
        input.postLoginUrlContains = postLoginUrlContains.trim();
      }
      const state = await captureAuthState(input);
      // Best-effort: zero the password from local React state before
      // the dialog closes, so an in-memory snapshot doesn't surface it.
      setPassword("");
      onCaptured(state);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-state-capture-title"
    >
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col overflow-y-auto rounded-lg border border-slate-700 bg-slate-950 p-5 shadow-2xl">
        <h2
          id="auth-state-capture-title"
          className="text-base font-semibold text-slate-100"
        >
          Capture login session
        </h2>
        <p className="mt-1 text-sm text-slate-400">
          UAIP will navigate to the login URL, fill the selectors with your
          credentials, capture the resulting session, and store it
          encrypted. The crawler reuses it to walk authenticated pages.
        </p>

        <div className="mt-3 rounded border border-amber-800/50 bg-amber-950/30 p-2 text-xs text-amber-200">
          <strong>Use a dedicated test account.</strong> Never your prod
          password. UAIP encrypts the captured session at rest; raw
          credentials are <em>only</em> persisted if you explicitly tick
          the &ldquo;remember&rdquo; box below.
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 text-sm">
          <Field label="Label">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="acme — qa-bot user"
              disabled={busy}
              className={INPUT}
            />
          </Field>
          <Field label="Login URL">
            <div className="flex gap-2">
              <input
                type="url"
                value={loginUrl}
                onChange={(e) => setLoginUrl(e.target.value)}
                placeholder="https://app.acme.test/login"
                disabled={busy || detecting}
                className={`${INPUT_MONO} flex-1`}
              />
              <button
                type="button"
                onClick={() => void handleDetect()}
                disabled={busy || detecting || !loginUrl.trim()}
                className="shrink-0 rounded-md border border-emerald-600/60 bg-emerald-700/20 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-700/40 disabled:opacity-50"
                title="Probe the login URL and pre-fill the three selectors below"
              >
                {detecting ? "Detecting…" : "🔍 Auto-detect form"}
              </button>
            </div>
            {pageTitle && !detectErr && (
              <p className="mt-1 text-[11px] text-emerald-300">
                Detected on: <span className="font-mono">{pageTitle}</span>
              </p>
            )}
            {detectErr !== null && (
              <p className="mt-1 text-[11px] text-amber-300">{detectErr}</p>
            )}
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Username selector"
              badge={detectedKinds.username}
            >
              <input
                type="text"
                value={usernameSelector}
                onChange={(e) => {
                  setUsernameSelector(e.target.value);
                  // Manual edit clears the auto-detected badge.
                  setDetectedKinds((k) => ({ ...k, username: undefined }));
                }}
                disabled={busy}
                className={INPUT_MONO}
              />
            </Field>
            <Field
              label="Password selector"
              badge={detectedKinds.password}
            >
              <input
                type="text"
                value={passwordSelector}
                onChange={(e) => {
                  setPasswordSelector(e.target.value);
                  setDetectedKinds((k) => ({ ...k, password: undefined }));
                }}
                disabled={busy}
                className={INPUT_MONO}
              />
            </Field>
          </div>
          <Field label="Submit selector" badge={detectedKinds.submit}>
            <input
              type="text"
              value={submitSelector}
              onChange={(e) => {
                setSubmitSelector(e.target.value);
                setDetectedKinds((k) => ({ ...k, submit: undefined }));
              }}
              disabled={busy}
              className={INPUT_MONO}
            />
          </Field>

          {/* Newbie-friendly guide. Collapsed by default — most users
           *  who already know CSS won't need it. <details> needs no
           *  React state and ships with native keyboard a11y. */}
          <details className="rounded-md border border-slate-800 bg-slate-900/40 p-2 text-xs text-slate-300 open:bg-slate-900/60">
            <summary className="cursor-pointer select-none text-slate-200 hover:text-white">
              Need help picking selectors? <span className="text-slate-500">(CSS, text, role, XPath)</span>
            </summary>
            <div className="mt-2 space-y-2">
              <p className="text-slate-400">
                Any field above accepts these four formats. Pick whichever
                matches what&apos;s stable on your login page.
              </p>
              <SelectorHint
                title="1. Text — easiest"
                example={`text=Log Masuk`}
                hint="Exact text match. Append `i` for case-insensitive: text=/Log Masuk/i"
              />
              <SelectorHint
                title="2. Role — most resilient to redesigns"
                example={`role=button[name="Log Masuk"]`}
                hint="Uses the accessible name. Works for <button>, <a role=button>, <input type=submit> uniformly."
              />
              <SelectorHint
                title="3. CSS with text-pseudo (Playwright)"
                example={`button:has-text("Log Masuk")`}
                hint="Constrain by tag AND text. Best when the page has many text matches."
              />
              <SelectorHint
                title="4. XPath — full power, slow to write"
                example={`xpath=//button[contains(text(), 'Log Masuk')]`}
                hint="MUST start with xpath= prefix or it gets parsed as CSS. Note: text() only matches direct children — wrap deeply-nested text in .//*[contains(text(),...)] instead."
              />
              <p className="text-slate-500 italic">
                Common picks: <code className="font-mono text-slate-300">input[type=email]</code>,{" "}
                <code className="font-mono text-slate-300">input[type=password]</code>,{" "}
                <code className="font-mono text-slate-300">button[type=submit]</code>,{" "}
                <code className="font-mono text-slate-300">text=Sign in</code>.
              </p>
            </div>
          </details>
          <Field label="Post-login URL contains (optional)">
            <input
              type="text"
              value={postLoginUrlContains}
              onChange={(e) => setPostLoginUrlContains(e.target.value)}
              placeholder="/dashboard"
              disabled={busy}
              className={INPUT_MONO}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              Helps detect a successful login on SPAs. Leave blank to
              wait for network-idle after submit.
            </p>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Username">
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="off"
                disabled={busy}
                className={INPUT}
              />
            </Field>
            <Field label="Password">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                disabled={busy}
                className={INPUT}
              />
            </Field>
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-400">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={busy}
              className="mt-0.5"
            />
            <span>
              <strong className="text-slate-200">Remember credentials</strong>{" "}
              so UAIP can silently re-login when this session expires.
              Stored encrypted with AES-GCM. Off = use-once; you&apos;ll
              re-enter creds when cookies expire.
            </span>
          </label>
        </div>

        {err !== null && (
          <p className="mt-3 text-sm text-rose-300">{err}</p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
            className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:bg-indigo-900 disabled:text-indigo-400 disabled:cursor-not-allowed"
          >
            {busy ? "Logging in…" : "Capture"}
          </button>
        </div>
      </div>
    </div>
  );
}

const INPUT =
  "w-full rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100 focus:border-indigo-500 focus:outline-none disabled:opacity-50";
const INPUT_MONO = `${INPUT} font-mono`;

function SelectorHint(props: {
  title: string;
  example: string;
  hint: string;
}): JSX.Element {
  return (
    <div>
      <div className="font-medium text-slate-200">{props.title}</div>
      <code className="mt-0.5 block break-all rounded bg-slate-950 px-2 py-1 font-mono text-[11px] text-emerald-300">
        {props.example}
      </code>
      <p className="mt-0.5 text-[11px] text-slate-500">{props.hint}</p>
    </div>
  );
}

function Field(props: {
  label: string;
  children: React.ReactNode;
  /** Optional "auto-detected as X" badge — drives a small green pill
   *  next to the field label after the user clicks Auto-detect. */
  badge?: LoginSelectorKind | undefined;
}): JSX.Element {
  return (
    <div>
      <div className="flex items-center gap-2">
        <label className="text-xs uppercase tracking-wide text-slate-400">
          {props.label}
        </label>
        {props.badge && (
          <span className="rounded-full border border-emerald-600/50 bg-emerald-900/30 px-1.5 py-0.5 text-[10px] font-medium text-emerald-200">
            auto · {props.badge}
          </span>
        )}
      </div>
      <div className="mt-1">{props.children}</div>
    </div>
  );
}
